import { useState, useRef, useEffect } from "react";
import type { Job, Take, VideoJobCreate, H3Aspect, H3Quality, H3Gpu } from "../lib/types";
import {
  H3_ASPECTS, H3_QUALITIES, H3_GPUS, H3_MAX_SECONDS, H3_MIN_SECONDS,
} from "../lib/types";
import { getUploadUrl, uploadFile, setVoiceVideoRefs } from "../lib/api";
import { useDataCache } from "../contexts/DataCacheContext";
import { buildDefaultPrompt } from "../lib/h3DefaultPrompt";
import { Icon } from "./icons";

interface VideoConfigPanelProps {
  job: Job;
  initialValues?: VideoJobCreate;
  onSubmit: (data: VideoJobCreate) => Promise<void>;
  onClose: () => void;
}

// Mirrors H3 Studio's chips (static/index.html).
const QUALITY_LABEL: Record<H3Quality, string> = {
  fast: "Fast",
  quality: "High",
  "2k": "2K",
};

// Megapixel budget per tier, same as workflows.dimensions() — a budget rather
// than a fixed long edge, so a clip costs the same whatever shape it is.
const MEGAPIXELS: Record<H3Quality, number> = { fast: 0.4, quality: 1.03, "2k": 2.36 };
const MAX_EDGE: Partial<Record<H3Quality, number>> = { "2k": 2048 };
const RATIO: Record<H3Aspect, [number, number]> = {
  "16:9": [16, 9], "9:16": [9, 16], "1:1": [1, 1],
  "4:3": [4, 3], "3:4": [3, 4], "21:9": [21, 9],
};

function dimensions(aspect: H3Aspect, quality: H3Quality): [number, number] {
  const [aw, ah] = RATIO[aspect] ?? [16, 9];
  let k = Math.sqrt((MEGAPIXELS[quality] * 1_000_000) / (aw * ah));
  const cap = MAX_EDGE[quality];
  if (cap) k = Math.min(k, cap / Math.max(aw, ah));
  return [
    Math.max(32, Math.round((aw * k) / 32) * 32),
    Math.max(32, Math.round((ah * k) / 32) * 32),
  ];
}

// A single-take job has nothing to disambiguate, so it reads as "the take".
function takeLabel(take: Take | undefined, total: number): string {
  if (!take) return "No completed take";
  return total > 1 ? `Take ${take.takeIndex + 1}` : "The take";
}

const MAX_REF_IMAGES = 9;
const MAX_REF_VIDEOS = 3;

interface Uploaded {
  key: string;
  preview: string;
  /** What this reference is, e.g. "the man", "the kitchen", "his jacket". */
  label?: string;
}

export function VideoConfigPanel({ job, initialValues, onSubmit, onClose }: VideoConfigPanelProps) {
  const [prompt, setPrompt] = useState(initialValues?.prompt ?? "");
  // Once the user types, the prefill stops rewriting itself under them.
  const [promptEdited, setPromptEdited] = useState(Boolean(initialValues?.prompt));
  const [aspect, setAspect] = useState<H3Aspect>(initialValues?.aspect ?? "16:9");
  const [quality, setQuality] = useState<H3Quality>(initialValues?.quality ?? "fast");
  const [gpu, setGpu] = useState<H3Gpu>(initialValues?.gpu ?? "H200");
  // Reload reopens the panel with a previous job's settings. Reference images
  // are settings, so restore them — pairing each stored key with the presigned
  // preview the read endpoint returned. A key with no URL still counts as a
  // reference (it will be sent) and just shows an empty tile.
  const restored = (keys?: string[], urls?: string[], labels?: string[]): Uploaded[] =>
    (keys ?? []).map((key, i) => ({
      key, preview: urls?.[i] ?? "", label: labels?.[i],
    }));

  // Reference images saved against the voice this job used. They preload when
  // the panel opens fresh, so a voice with saved references never has to have
  // them re-uploaded. Reload's own values win — it is restoring a specific
  // past job, not starting a new one.
  const { references, providedRefs, refreshRefs } = useDataCache();
  const voice = [...references, ...providedRefs].find(
    (r) => r.name === job.referenceName,
  );
  const savedRefs = voice?.videoRefImages ?? [];

  const [images, setImages] = useState<Uploaded[]>(() => {
    if (initialValues?.refImageKeys?.length) {
      return restored(
        initialValues.refImageKeys,
        initialValues.refImageUrls,
        initialValues.refImageLabels,
      );
    }
    return savedRefs.map((r, i) => ({
      key: r.key,
      preview: voice?.videoRefImageUrls?.[i] ?? "",
      label: r.label,
    }));
  });
  const [videos, setVideos] = useState<Uploaded[]>(
    restored(initialValues?.refVideoKeys, initialValues?.refVideoUrls, initialValues?.refVideoLabels),
  );
  const [audioStartS, setAudioStartS] = useState(initialValues?.audioStartS ?? 0);
  const [scene, setScene] = useState(initialValues?.scene ?? "");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savingRefs, setSavingRefs] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Which take's audio drives the video. Only completed takes are offerable;
  // normalizeTakes() on the server guarantees takes[] exists even for legacy
  // single-output jobs, so this covers both shapes.
  const takes = (job.takes ?? []).filter((t) => t.status === "complete" && t.outputKey);
  const [takeIndex, setTakeIndex] = useState<number | undefined>(
    initialValues?.takeIndex ?? takes[0]?.takeIndex,
  );
  const take = takes.find((t) => t.takeIndex === takeIndex) ?? takes[0];

  // The chosen take's length decides whether a window has to be picked.
  const audioLen = take?.duration ?? job.duration ?? 0;
  const needsWindow = audioLen > H3_MAX_SECONDS;
  const maxStart = Math.max(0, audioLen - H3_MAX_SECONDS);
  const windowLen = Math.max(
    H3_MIN_SECONDS,
    Math.min(audioLen - audioStartS, H3_MAX_SECONDS),
  );

  // A start offset chosen against one take is meaningless against another.
  useEffect(() => {
    setAudioStartS(0);
  }, [takeIndex]);

  // Prefill a complete MiniMax full-reference rewrite for Chatter's one video
  // case: this person, saying this audio, in sync, with nothing else audible.
  // It depends on the reference count and on whether the take is being
  // windowed, so it is rebuilt as those change — until the user edits it.
  useEffect(() => {
    if (promptEdited) return;
    setPrompt(
      buildDefaultPrompt({
        refImageCount: images.length,
        refVideoCount: videos.length,
        refs: images.map((i) => ({ label: i.label })),
        videoRefs: videos.map((v) => ({ label: v.label })),
        scene,
        text: job.text,
        language: job.language,
        windowed: needsWindow,
      }),
    );
    // Labels and scene are joined so a rename rebuilds the prompt without
    // making the dependency list depend on object identity.
  }, [promptEdited, images.map((i) => i.label ?? "").join("\u0000"),
      videos.map((v) => v.label ?? "").join("\u0000"),
      images.length, videos.length, scene, job.text, job.language, needsWindow]);

  const [w, h] = dimensions(aspect, quality);

  const upload = async (
    file: File,
    kind: "image" | "video",
  ): Promise<void> => {
    const isImage = kind === "image";
    if (isImage && !file.type.startsWith("image/")) {
      setUploadError("Please select an image file");
      return;
    }
    if (!isImage && !file.type.startsWith("video/")) {
      setUploadError("Please select a video file");
      return;
    }
    const limit = isImage ? 10 : 100;
    if (file.size > limit * 1024 * 1024) {
      setUploadError(`${isImage ? "Image" : "Video"} must be under ${limit} MB`);
      return;
    }

    setUploadError(null);
    setUploading(true);
    try {
      const { uploadUrl, key } = await getUploadUrl(
        isImage ? "reference-image" : "reference-video",
        file.type,
        file.name,
      );
      await uploadFile(uploadUrl, file, file.type);
      const entry = { key, preview: URL.createObjectURL(file) };
      if (isImage) setImages((prev) => [...prev, entry].slice(0, MAX_REF_IMAGES));
      else setVideos((prev) => [...prev, entry].slice(0, MAX_REF_VIDEOS));
    } catch (err) {
      setUploadError("Upload failed — please try again");
      console.error(`${kind} upload failed:`, err);
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files: FileList | null, kind: "image" | "video") => {
    if (!files) return;
    const room = kind === "image"
      ? MAX_REF_IMAGES - images.length
      : MAX_REF_VIDEOS - videos.length;
    Array.from(files).slice(0, room).forEach((f) => void upload(f, kind));
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      setSubmitError("Motion prompt is required");
      return;
    }
    if (images.length === 0 && videos.length === 0) {
      setSubmitError("Add at least one reference image or video");
      return;
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        prompt: prompt.trim(),
        aspect,
        quality,
        gpu,
        refImageKeys: images.map((i) => i.key),
        refVideoKeys: videos.map((v) => v.key),
        refImageLabels: images.map((i) => i.label ?? ""),
        refVideoLabels: videos.map((v) => v.label ?? ""),
        scene: scene.trim() || undefined,
        audioStartS: needsWindow ? audioStartS : 0,
        takeIndex,
      });
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Whether what is on screen already matches what is saved against the voice.
  const savedMatches =
    savedRefs.length === images.length &&
    savedRefs.every(
      (r, i) => r.key === images[i]?.key && (r.label ?? "") === (images[i]?.label ?? ""),
    );

  const handleSaveToVoice = async () => {
    if (!job.referenceName || savingRefs) return;
    setSavingRefs(true);
    setSubmitError(null);
    try {
      await setVoiceVideoRefs(
        job.referenceName,
        images.map((i) => ({ key: i.key, label: i.label?.trim() || undefined })),
      );
      await refreshRefs();
      setSavedNotice(
        images.length
          ? `Saved to ${job.referenceName} — these load automatically next time.`
          : `Cleared the saved references for ${job.referenceName}.`,
      );
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Could not save to the voice",
      );
    } finally {
      setSavingRefs(false);
    }
  };

  const setLabel = (kind: "image" | "video", key: string, label: string) => {
    const update = (p: Uploaded[]) =>
      p.map((x) => (x.key === key ? { ...x, label } : x));
    if (kind === "image") setImages(update);
    else setVideos(update);
    setSavedNotice(null);
  };

  const tile = (item: Uploaded, kind: "image" | "video", idx: number) => (
    <div key={item.key} style={{ display: "flex", flexDirection: "column", gap: 4, position: "relative" }}>
      <div style={{ position: "relative" }}>
      {kind === "image" ? (
        <img
          src={item.preview}
          alt={`Reference ${idx + 1}`}
          style={{
            width: 64, height: 64, objectFit: "cover",
            borderRadius: 8, border: "1px solid var(--hair-2)",
          }}
        />
      ) : (
        <video
          src={item.preview}
          muted
          style={{
            width: 64, height: 64, objectFit: "cover",
            borderRadius: 8, border: "1px solid var(--hair-2)",
          }}
        />
      )}
      <button
        type="button"
        aria-label={`Remove reference ${idx + 1}`}
        onClick={() =>
          kind === "image"
            ? setImages((p) => p.filter((x) => x.key !== item.key))
            : setVideos((p) => p.filter((x) => x.key !== item.key))
        }
        style={{
          position: "absolute", top: -6, right: -6,
          width: 20, height: 20, borderRadius: 10, lineHeight: "18px",
          border: "1px solid var(--hair-2)", background: "oklch(0.18 0.014 265)",
          color: "var(--muted)", cursor: "pointer", fontSize: 12, padding: 0,
        }}
      >
        ×
      </button>
      </div>
      <input
        type="text"
        className="input"
        value={item.label ?? ""}
        onChange={(e) => setLabel(kind, item.key, e.target.value)}
        placeholder={idx === 0 ? "the person" : "what is this?"}
        aria-label={`What reference ${idx + 1} is`}
        style={{ width: 96, fontSize: 11, padding: "4px 6px" }}
      />
    </div>
  );

  return (
    <div
      style={{
        marginTop: 4,
        padding: 16,
        borderRadius: 10,
        background: "oklch(0.18 0.014 265)",
        border: "1px solid var(--hair)",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {/* Reference images */}
      <div>
        <div className="label">
          Reference images{" "}
          <span style={{ color: "var(--muted)", fontWeight: 400 }}>
            ({images.length}/{MAX_REF_IMAGES}) — label each one so it gets used
          </span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          {images.map((it, i) => tile(it, "image", i))}
          {images.length < MAX_REF_IMAGES && (
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="upload-zone"
              style={{ width: 64, height: 64, minHeight: 0, padding: 0 }}
              aria-label="Add reference image"
            >
              {uploading ? <span className="s">…</span> : <Icon.Image />}
            </button>
          )}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              handleFiles(e.target.files, "image");
              e.target.value = "";
            }}
          />
        </div>
        {job.referenceName && !voice?.provided && (
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 11, padding: "3px 8px" }}
              onClick={handleSaveToVoice}
              disabled={savingRefs || savedMatches}
              title={`Store these against the voice "${job.referenceName}" so they load by default`}
            >
              {savingRefs
                ? "Saving…"
                : savedMatches && savedRefs.length
                  ? "✓ Saved to this voice"
                  : `Save these to ${job.referenceName}`}
            </button>
            {savedNotice && (
              <span style={{ fontSize: 11, color: "var(--muted)" }}>{savedNotice}</span>
            )}
          </div>
        )}
      </div>

      {/* Reference videos */}
      <div>
        <div className="label">
          Reference videos{" "}
          <span style={{ color: "var(--muted)", fontWeight: 400 }}>
            ({videos.length}/{MAX_REF_VIDEOS}, optional)
          </span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          {videos.map((it, i) => tile(it, "video", i))}
          {videos.length < MAX_REF_VIDEOS && (
            <button
              type="button"
              onClick={() => videoInputRef.current?.click()}
              className="upload-zone"
              style={{ width: 64, height: 64, minHeight: 0, padding: 0 }}
              aria-label="Add reference video"
            >
              {uploading ? <span className="s">…</span> : <Icon.Image />}
            </button>
          )}
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              handleFiles(e.target.files, "video");
              e.target.value = "";
            }}
          />
        </div>
        {uploadError && (
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--red)" }}>{uploadError}</div>
        )}
      </div>

      {/* Scene */}
      <div>
        <label className="label" htmlFor="video-scene">
          Scene <span style={{ color: "var(--muted)", fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea
          id="video-scene"
          value={scene}
          onChange={(e) => setScene(e.target.value)}
          placeholder="Where this happens and what the shot looks like — e.g. 'standing in the kitchen from Picture 2, leaning on the counter, warm evening light, camera slowly pushes in'"
          rows={2}
          className="input"
          style={{ resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
        />
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
          Leave empty for a plain talking-head shot. Anything here replaces that
          default framing rather than being added next to it.
        </div>
      </div>

      {/* Motion prompt */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
          <label className="label" htmlFor="video-motion-prompt">
            Prompt
          </label>
          {promptEdited && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 11, padding: "2px 8px" }}
              onClick={() => setPromptEdited(false)}
            >
              Reset to default
            </button>
          )}
        </div>
        <textarea
          id="video-motion-prompt"
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            setPromptEdited(true);
          }}
          placeholder="Describe the motion (e.g. 'person speaking, subtle head movement')"
          rows={10}
          className="input"
          style={{ resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
        />
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
          {promptEdited
            ? "Edited. Sent to the model as written."
            : job.text && !needsWindow
              ? "Prefilled for exact likeness and lip sync, with your script included."
              : needsWindow
                ? "Prefilled for exact likeness and lip sync. The script is left out because only part of the take is animated."
                : "Prefilled for exact likeness and lip sync."}
        </div>
      </div>

      {/* Aspect ratio */}
      <div>
        <div className="label">Aspect ratio</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {H3_ASPECTS.map((a) => (
            <button
              key={a}
              type="button"
              className={`chip${a === aspect ? " on" : ""}`}
              onClick={() => setAspect(a)}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* Resolution */}
      <div>
        <div className="label">Resolution</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {H3_QUALITIES.map((q) => (
            <button
              key={q}
              type="button"
              className={`chip${q === quality ? " on" : ""}`}
              onClick={() => setQuality(q)}
            >
              {QUALITY_LABEL[q]}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
          {w}×{h}
          {quality === "2k" && " · ~2.3× the work of High, and billed for it"}
        </div>
      </div>

      {/* GPU */}
      <div>
        <div className="label">GPU</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {H3_GPUS.map((g) => (
            <button
              key={g}
              type="button"
              className={`chip${g === gpu ? " on" : ""}`}
              onClick={() => setGpu(g)}
            >
              {g}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
          H200 is both the fastest and the cheapest per clip.
        </div>
      </div>

      {/* Audio window — only when the take is longer than the model can cover */}
      <div>
        <div className="label">Audio</div>
        {needsWindow ? (
          <>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 6 }}>
              {takeLabel(take, takes.length)} is {audioLen.toFixed(1)}s and the model
              generates at most {H3_MAX_SECONDS}s. Choose which part to animate.
            </div>
            <input
              type="range"
              min={0}
              max={maxStart}
              step={0.1}
              value={audioStartS}
              onChange={(e) => setAudioStartS(parseFloat(e.target.value))}
              style={{ width: "100%" }}
              aria-label="Audio window start"
            />
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
              {audioStartS.toFixed(1)}s – {(audioStartS + windowLen).toFixed(1)}s
              {"  ·  "}
              {windowLen.toFixed(1)}s clip
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: "var(--muted)" }}>
            {takeLabel(take, takes.length)} is used as the reference voice, and the exact
            audio is kept in the finished video.
          </div>
        )}
      </div>

      {submitError && <div className="err-banner">{submitError}</div>}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" }}>
        {takes.length > 1 && (
          <label
            style={{ display: "flex", alignItems: "center", gap: 6, marginRight: "auto" }}
          >
            <span className="label" style={{ margin: 0 }}>Audio</span>
            <select
              className="input"
              style={{ width: "auto", padding: "6px 8px" }}
              value={takeIndex ?? ""}
              onChange={(e) => setTakeIndex(Number(e.target.value))}
              aria-label="Which take to use as the reference audio"
            >
              {takes.map((t) => (
                <option key={t.takeIndex} value={t.takeIndex}>
                  {takeLabel(t, takes.length)}
                  {t.duration ? ` · ${t.duration.toFixed(1)}s` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={
            submitting || uploading || !take ||
            (images.length === 0 && videos.length === 0)
          }
        >
          {submitting ? "Starting…" : (
            <>
              <Icon.Image /> Generate video
            </>
          )}
        </button>
      </div>
    </div>
  );
}
